import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTrackAgentPillClicked } from '@air/analytics';

import { usePrivateWorkspaceAgentMessageListContext } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentMessageListContext';
import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { WorkspaceAgentUncitedAssetScope } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/scope/WorkspaceAgentUncitedAssetScope';
import { useOpenWorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetPill';
import { usePrivateWorkspaceAgentAssetListThumbnails } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentAssetListThumbnails';
import { usePrivateWorkspaceAgentHydratedArtifactPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentHydratedArtifactPill';
import { useRegisterWorkspaceAgentAssetPillClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useRegisterWorkspaceAgentAssetPillClipIds';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { flattenWorkspaceAgentAssetPillItems } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/utils/scrollWorkspaceAgentDisplayRowStartIntoView';
import {
  isWorkspaceAgentArtifactHydrationPill,
  workspaceAgentArtifactPillNeedsRowHydration,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentArtifactHydration';

export type PrivateMessageUncitedAssetScopeProps = {
  pills: readonly WorkspaceAgentMessagePill[];
};

type ArtifactPillHydrationSlotState = {
  isPending: boolean;
  isUnavailable: boolean;
  pill: WorkspaceAgentMessagePill;
};

type UncitedArtifactPillHydrationSlotProps = {
  onUpdate: (pillId: string, state: ArtifactPillHydrationSlotState) => void;
  pill: WorkspaceAgentMessagePill;
};

const UncitedArtifactPillHydrationSlot = memo(({ onUpdate, pill }: UncitedArtifactPillHydrationSlotProps) => {
  const {
    inViewRef,
    isPending,
    isUnavailable,
    pill: hydrated,
  } = usePrivateWorkspaceAgentHydratedArtifactPill({
    isStreaming: false,
    pill,
  });

  useEffect(() => {
    onUpdate(pill.id, { isPending, isUnavailable, pill: hydrated });
  }, [hydrated, isPending, isUnavailable, onUpdate, pill.id]);

  return <span aria-hidden className="sr-only" ref={inViewRef} />;
});

UncitedArtifactPillHydrationSlot.displayName = 'UncitedArtifactPillHydrationSlot';

export const PrivateMessageUncitedAssetScope = memo(({ pills }: PrivateMessageUncitedAssetScopeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollDisplayRowStartIntoView } = usePrivateWorkspaceAgentMessageListContext();
  const [hydrationByPillId, setHydrationByPillId] = useState<Record<string, ArtifactPillHydrationSlotState>>({});

  const hydrationTargets = useMemo(
    () =>
      pills.filter(
        (pill) => isWorkspaceAgentArtifactHydrationPill(pill) && workspaceAgentArtifactPillNeedsRowHydration(pill),
      ),
    [pills],
  );

  const pillsIdentityKey = useMemo(() => pills.map((pill) => pill.id).join(','), [pills]);
  const lastPillsIdentityKeyRef = useRef(pillsIdentityKey);

  if (lastPillsIdentityKeyRef.current !== pillsIdentityKey) {
    lastPillsIdentityKeyRef.current = pillsIdentityKey;
    setHydrationByPillId({});
  }

  const handleHydrationUpdate = useCallback((pillId: string, state: ArtifactPillHydrationSlotState) => {
    setHydrationByPillId((previous) => {
      const existing = previous[pillId];

      if (
        existing &&
        existing.isPending === state.isPending &&
        existing.isUnavailable === state.isUnavailable &&
        existing.pill === state.pill
      ) {
        return previous;
      }

      return { ...previous, [pillId]: state };
    });
  }, []);

  const displayPills = useMemo(
    () => pills.map((pill) => hydrationByPillId[pill.id]?.pill ?? pill),
    [hydrationByPillId, pills],
  );

  const isHydrationPending =
    hydrationTargets.length > 0 && hydrationTargets.some((pill) => hydrationByPillId[pill.id]?.isPending ?? true);
  const isHydrationUnavailable =
    hydrationTargets.length > 0 && hydrationTargets.some((pill) => hydrationByPillId[pill.id]?.isUnavailable);

  const unavailableReferenceId =
    hydrationTargets.find((pill) => hydrationByPillId[pill.id]?.isUnavailable)?.id ??
    displayPills[0]?.id ??
    'uncited-assets';

  useRegisterWorkspaceAgentAssetPillClipIds(displayPills);

  const assetItems = useMemo(() => flattenWorkspaceAgentAssetPillItems(displayPills), [displayPills]);
  const declaredAssetCount = useMemo(() => {
    let count = 0;

    for (const pill of displayPills) {
      if (pill.type === 'asset') {
        count += 1;
        continue;
      }

      if (pill.type === 'assetList') {
        count += pill.count;
      }
    }

    return count;
  }, [displayPills]);

  const { thumbnails, unavailableCount } = usePrivateWorkspaceAgentAssetListThumbnails(assetItems, {
    isStreaming: false,
  });
  const { openAssetClip } = useOpenWorkspaceAgentAssetPill();
  const { trackAgentPillClicked } = useTrackAgentPillClicked();

  const itemCount = assetItems.length > 0 ? assetItems.length : declaredAssetCount;

  const handleOpenThumbnail = useCallback(
    (id: string) => {
      if (!assetItems.some((item) => item.clipId === id)) {
        return;
      }

      const thumbnail = thumbnails.find((entry) => entry.id === id);
      trackAgentPillClicked({
        object: 'asset_list',
        object_count: itemCount,
        object_name: thumbnail?.label ?? 'Selected assets',
      });
      openAssetClip(id);
    },
    [assetItems, itemCount, openAssetClip, thumbnails, trackAgentPillClicked],
  );

  const handleExpandedChange = useCallback(
    (isExpanded: boolean) => {
      if (!isExpanded) {
        return;
      }

      const rowElement = containerRef.current?.closest<HTMLElement>(`[${WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE}]`);
      const displayRowId = rowElement?.getAttribute(WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE);

      if (displayRowId) {
        scrollDisplayRowStartIntoView(displayRowId);
      }
    },
    [scrollDisplayRowStartIntoView],
  );

  const hydrationSlots = hydrationTargets.map((pill) => (
    <UncitedArtifactPillHydrationSlot key={pill.id} onUpdate={handleHydrationUpdate} pill={pill} />
  ));

  if (isHydrationPending) {
    return (
      <>
        {hydrationSlots}
        <span className="flex w-full min-w-0 justify-end">
          <WorkspaceAgentPendingPill label="Assets" />
        </span>
      </>
    );
  }

  if (isHydrationUnavailable) {
    return (
      <>
        {hydrationSlots}
        <WorkspaceAgentUnavailablePill count={declaredAssetCount} kind="asset" referenceId={unavailableReferenceId} />
      </>
    );
  }

  return (
    <>
      {hydrationSlots}
      <WorkspaceAgentUncitedAssetScope
        containerRef={containerRef}
        itemCount={itemCount}
        onExpandedChange={handleExpandedChange}
        onOpenThumbnail={handleOpenThumbnail}
        thumbnails={thumbnails}
        unavailableCount={unavailableCount}
      />
    </>
  );
});

PrivateMessageUncitedAssetScope.displayName = 'PrivateMessageUncitedAssetScope';
